import { useRouter } from 'next/router';
import { logEvent } from '@/analytics';
import { useGetUser } from '@/queries/user';
import { ETF_TAVAN_ISIN } from '@/modules/invest/subModules/fund/constants';
import { openCompleteSejamNoticeBottomSheet } from '@/components/CompleteSejamNoticeBottomSheet';
import { openLeverageFundWarningBottomSheet } from '@/modules/invest/subModules/fund/components/LeverageFundWarningBottomSheet';
import { useGetFundCodeFromQuery } from '@/modules/invest/hooks/useGetFundCodeFromQuery';
import { useGetFundDetails } from '@/modules/invest/subModules/fund/hooks/useGetFundDetails';

export const useHandleFundActions = () => {
  const { push } = useRouter();

  const { data: user } = useGetUser();

  const fundCode = useGetFundCodeFromQuery();
  const { fundDetails } = useGetFundDetails(fundCode);

  // * Log analytic events
  const handleLogEvent = (type: 'issuance' | 'redemption') => {
    const eventName =
      type === 'issuance' ? 'FUNDS_ISSUANCE' : 'FUNDS_REDEMPTION';

    const hasAsset = fundDetails && fundDetails?.count > 0;

    const status = hasAsset ? 'HAVE_REMAIN' : 'NO_REMAIN';

    logEvent(eventName, {
      name: fundCode,
      status,
    });
  };

  // * Redirect based on fund type and order type
  const redirectToFundAction = (type: 'issuance' | 'redemption') => {
    const etfOrderType = type === 'issuance' ? 'buy' : 'sell';

    const path =
      fundDetails?.type === 'FUND'
        ? `/invest/fund/${type}/${fundCode}`
        : `/invest/etf/${etfOrderType}/${fundCode}`;
    push(path);
  };

  const handleIssuance = () => {
    if (!fundDetails) return;

    handleLogEvent('issuance');

    if (!user?.isSejami) {
      openCompleteSejamNoticeBottomSheet({
        text: 'لطفا جهت خرید صندوق، ابتدا قرارداد الکترونیک خود را امضا و سپس نسبت به ثبت درخواست، اقدام نمائید.',
        redirectUrl: `/invest/fund/${fundCode}`,
        primaryButtonProps: {
          children: 'امضای قرارداد الکترونیک',
          className: '!w-1/2',
        },
        secondaryButtonProps: {
          className: '!w-1/2',
          variant: 'outline',
        },
      });
    } else if (fundCode === ETF_TAVAN_ISIN) {
      openLeverageFundWarningBottomSheet();
    } else {
      redirectToFundAction('issuance');
    }
  };

  const handleRedemption = () => {
    if (!fundDetails) return;

    handleLogEvent('redemption');
    redirectToFundAction('redemption');
  };

  const isIssuanceAvailable = !!fundDetails?.fundIssuance;
  const isRedemptionAvailable =
    fundDetails && fundDetails.fundRedemption && fundDetails.count > 0;

  return {
    isIssuanceAvailable,
    handleIssuance,
    handleRedemption,
    isRedemptionAvailable,
  };
};
